import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Lock } from 'lucide-react';
import { api, type TenantBase } from '../lib/api';
import { ALL_MODULE_KEYS, MODULE_CATALOG, moduleLabel, OPT_IN_MODULES } from '../lib/moduleCatalog';
import { Button, Card, ErrorText, Modal, Switch } from './ui';
import { AppliedHint } from './AppliedHint';

/** The regular modules: what "all modules" covers. */
const REGULAR = MODULE_CATALOG.filter((m) => !m.optIn);

/**
 * Modules built for one customer (e.g. Actori for Genius Media). Off for every
 * tenant until granted here, whatever the modules list says; each switch saves
 * at once and the instance applies it at its next heartbeat.
 */
function OptInModulesCard({ tenant }: { tenant: TenantBase }) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (granted: string[]) => api.setTenantOptInModules(tenant.id, granted),
    onSuccess: async () => {
      await queryClient.invalidateQueries();
    },
  });
  if (OPT_IN_MODULES.length === 0) return null;
  const granted = new Set(tenant.optInModules);

  function toggle(key: string, on: boolean) {
    const next = new Set(granted);
    if (on) next.add(key);
    else next.delete(key);
    mutation.mutate([...next]);
  }

  return (
    <Card className="p-0">
      <ul className="divide-y divide-slate-800/70">
        <li className="px-4 py-3">
          <div className="text-sm font-medium text-white">Module la cerere</div>
          <p className="mt-0.5 text-xs text-slate-400">
            Făcute pentru un anumit client. Sunt oprite la toți ceilalți, chiar și cu „Toate
            modulele”; le pornești aici, una câte una.
          </p>
        </li>
        {OPT_IN_MODULES.map((m) => (
          <li key={m.key} className="flex items-center justify-between gap-3 px-4 py-2.5">
            <span className="text-sm font-medium text-white">{m.label}</span>
            <Switch
              checked={granted.has(m.key)}
              disabled={mutation.isPending}
              onChange={(next) => toggle(m.key, next)}
              label={moduleLabel(m.key)}
            />
          </li>
        ))}
      </ul>
      {mutation.error && (
        <div className="border-t border-slate-800/70 px-4 py-2.5">
          <ErrorText error={mutation.error} />
        </div>
      )}
    </Card>
  );
}

/**
 * Which modules this tenant bought. "All modules" means no ceiling (also new
 * modules added later); otherwise only the switched-on ones. The instance
 * applies the list at its next heartbeat; its own admin can still switch
 * modules off, never on beyond this list.
 */
export function ModulesTab({
  tenant,
  lastSeenAt,
}: {
  tenant: TenantBase;
  lastSeenAt: string | null;
}) {
  const queryClient = useQueryClient();
  const saved = tenant.allowedModules;
  const [all, setAll] = useState(saved === null);
  const [selected, setSelected] = useState<Set<string>>(() => new Set(saved ?? ALL_MODULE_KEYS));
  const [confirming, setConfirming] = useState(false);

  // Same shape the server stores: core always in, a child only with its
  // parent, catalogue order.
  const draft = useMemo<string[] | null>(() => {
    if (all) return null;
    const on = (key: string) => {
      const m = MODULE_CATALOG.find((x) => x.key === key);
      return Boolean(m?.core || selected.has(key));
    };
    return REGULAR.filter((m) => on(m.key) && (!m.parent || on(m.parent))).map((m) => m.key);
  }, [all, selected]);

  const dirty = draft === null || saved === null ? draft !== saved : draft.join() !== saved.join();

  const removed = draft ? ALL_MODULE_KEYS.filter((k) => !draft.includes(k)) : [];

  const mutation = useMutation({
    mutationFn: () => api.setTenantModules(tenant.id, draft),
    onSuccess: async () => {
      setConfirming(false);
      await queryClient.invalidateQueries();
    },
  });

  function toggle(key: string, on: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(key);
      else next.delete(key);
      return next;
    });
  }

  function reset() {
    setAll(saved === null);
    setSelected(new Set(saved ?? ALL_MODULE_KEYS));
  }

  return (
    <div className="space-y-4">
      <Card className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-sm font-medium text-white">Toate modulele</div>
          <p className="mt-0.5 text-xs text-slate-400">
            Fără restricții: clientul are tot, inclusiv modulele adăugate ulterior.
          </p>
        </div>
        <Switch checked={all} onChange={setAll} label="Toate modulele" />
      </Card>

      <Card className="p-0">
        <ul className="divide-y divide-slate-800/70">
          {REGULAR.map((m) => {
            const parent = m.parent ? MODULE_CATALOG.find((p) => p.key === m.parent) : undefined;
            const parentOff = Boolean(parent && !parent.core && !all && !selected.has(parent.key));
            const checked = all || m.core || (!parentOff && selected.has(m.key));
            return (
              <li
                key={m.key}
                className={`flex items-center justify-between gap-3 px-4 py-2.5 ${m.parent ? 'pl-10' : ''}`}
              >
                <div className="min-w-0">
                  <span
                    className={`text-sm ${m.parent ? 'text-slate-300' : 'font-medium text-white'} ${parentOff ? 'opacity-50' : ''}`}
                  >
                    {m.label}
                  </span>
                  {m.core && (
                    <span className="ml-2 inline-flex items-center gap-1 text-xs text-slate-500">
                      <Lock className="h-3 w-3" /> de bază, mereu pornit
                    </span>
                  )}
                  {parentOff && (
                    <span className="ml-2 text-xs text-slate-500">
                      oprit odată cu {parent!.label}
                    </span>
                  )}
                </div>
                <Switch
                  checked={checked}
                  disabled={all || m.core || parentOff}
                  onChange={(next) => toggle(m.key, next)}
                  label={moduleLabel(m.key)}
                />
              </li>
            );
          })}
        </ul>
      </Card>

      <OptInModulesCard tenant={tenant} />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <AppliedHint savedAt={tenant.updatedAt} lastSeenAt={lastSeenAt} />
        <div className="flex gap-2">
          <Button variant="ghost" disabled={!dirty} onClick={reset}>
            Renunță
          </Button>
          <Button disabled={!dirty} onClick={() => setConfirming(true)}>
            Salvează
          </Button>
        </div>
      </div>

      {confirming && (
        <Modal
          title={`Salvezi modulele pentru ${tenant.name}?`}
          onClose={() => setConfirming(false)}
        >
          <div className="space-y-4">
            {draft === null ? (
              <p className="text-sm text-slate-300">
                Clientul primește toate modulele, inclusiv cele adăugate ulterior.
              </p>
            ) : removed.length > 0 ? (
              <>
                <p className="text-sm text-slate-300">
                  La următorul heartbeat, aceste module dispar pentru toți utilizatorii instanței,
                  inclusiv pentru admin. Datele nu se șterg; reapar dacă pornești modulul la loc.
                </p>
                <ul className="list-inside list-disc text-sm text-slate-400">
                  {removed.map((k) => (
                    <li key={k}>{moduleLabel(k)}</li>
                  ))}
                </ul>
              </>
            ) : (
              <p className="text-sm text-slate-300">
                Toate modulele actuale rămân pornite; modulele adăugate ulterior nu vor fi incluse
                automat.
              </p>
            )}
            <ErrorText error={mutation.error} />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setConfirming(false)}>
                Renunță
              </Button>
              <Button disabled={mutation.isPending} onClick={() => mutation.mutate()}>
                Salvează
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
